import React from 'react';

const ConversationHeader = ({ conversation, onRerun }) => {
  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'badge-orange',
      running: 'badge-orange',
      passed: 'badge-green',
      failed: 'badge-red',
      errored: 'badge-gray'
    };
    return classes[status] || 'badge-gray';
  };

  const formatDuration = () => {
    if (!conversation.started_at) return 'Not started';
    
    const start = new Date(conversation.started_at);
    const end = conversation.finished_at ? new Date(conversation.finished_at) : new Date();
    const duration = Math.floor((end - start) / 1000);
    
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    
    return `${minutes}m ${seconds}s`;
  };

  const formatModel = () => {
    if (!conversation.vapi_model) return null;

    const provider = conversation.vapi_model.provider;
    const model = conversation.vapi_model.model;
    if (!provider && !model) return null;

    return provider ? `${provider}/${model}` : model;
  };

  return (
    <div className="relative bg-slate-950 text-white overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/20 via-slate-900 to-purple-900/20"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan-600/10 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-grid-slate-700/[0.04] [mask-image:linear-gradient(0deg,transparent,black)]"></div>
      
      <div className="relative px-6 py-5 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <h2 className="text-2xl font-bold text-white tracking-tight">
                Conversation
              </h2>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${
                conversation.status === 'running' ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50' :
                conversation.status === 'passed' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50' :
                conversation.status === 'failed' ? 'bg-red-500/20 text-red-300 border border-red-500/50' :
                'bg-slate-500/20 text-slate-300 border border-slate-500/50'
              }`}>
                {conversation.status === 'running' && (
                  <span className="mr-2 relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400"></span>
                  </span>
                )}
                {conversation.status}
              </span>
            </div>
            
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 group">
                  <svg className="w-4 h-4 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  <span className="font-mono text-xs text-slate-400 bg-slate-900/50 px-3 py-1 rounded border border-slate-800/50 group-hover:border-cyan-800/50 transition-colors">
                    {conversation.ping_uuid}
                  </span>
                </div>
                {conversation.ping_web_url && (
                  <a
                    href={conversation.ping_web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1 rounded-lg bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-800/50 hover:from-cyan-900/30 hover:to-blue-900/30 transition-all group"
                    title="View in Ping"
                  >
                    <svg className="w-4 h-4 text-cyan-400 group-hover:text-cyan-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span className="ml-2 text-xs font-medium text-cyan-400 group-hover:text-cyan-300 transition-colors">
                      View in Ping
                    </span>
                  </a>
                )}
              </div>
              
              <div className="h-4 w-px bg-gradient-to-b from-transparent via-slate-600 to-transparent"></div>
              
              <a 
                href={`/admin/scenario_collections/${conversation.scenario_collection_id}/scenarios/${conversation.scenario_id}`}
                className="group flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="relative">
                  {conversation.scenario_name}
                  <span className="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-cyan-400/0 via-cyan-400/70 to-cyan-400/0 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                </span>
              </a>
              
              <div className="h-4 w-px bg-gradient-to-b from-transparent via-slate-600 to-transparent"></div>
              
              <span className="flex items-center space-x-2 text-slate-400">
                <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-mono text-xs">{formatDuration()}</span>
              </span>

              {formatModel() && (
                <>
                  <div className="h-4 w-px bg-gradient-to-b from-transparent via-slate-600 to-transparent"></div>

                  <span className="flex items-center space-x-2 text-slate-400" title={`Model: ${formatModel()}`}>
                    <svg className="w-4 h-4 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 009.75 21h4.5A2.25 2.25 0 0016.5 18.75V5.25A2.25 2.25 0 0014.25 3h-4.5z" />
                    </svg>
                    <span className="font-mono text-xs text-slate-400 bg-slate-900/50 px-3 py-1 rounded border border-slate-800/50">
                      {formatModel()}
                    </span>
                  </span>
                </>
              )}
              
              {conversation.persona && (
                <>
                  <div className="h-4 w-px bg-gradient-to-b from-transparent via-slate-600 to-transparent"></div>
                  
                  <div className="flex items-center space-x-2 text-purple-400" title={conversation.persona.description}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-xs font-medium">{conversation.persona.name}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <div>
            <button
              onClick={onRerun}
              className="relative group overflow-hidden px-6 py-2.5 font-medium text-white transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 transition-all duration-300 group-hover:scale-110"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 opacity-0 group-hover:opacity-100 blur-xl transition-all duration-300"></div>
              <div className="relative flex items-center space-x-2">
                <svg className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="uppercase tracking-wider text-sm">Rerun</span>
              </div>
            </button>
          </div>
        </div>
        
        {conversation.error_message && (
          <div className="mt-4 relative overflow-hidden rounded-lg">
            <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-red-800/20 backdrop-blur-sm rounded-lg"></div>
            <div className="absolute inset-0 bg-red-500/5 rounded-lg"></div>
            <div className="relative p-3 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300 flex items-start font-mono">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>
                  <span className="text-red-400 font-bold">ERROR:</span> {conversation.error_message}
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
    </div>
  );
};

export default ConversationHeader;
